import { RouteComponentProps } from "../../../../models/RouteComponent";
import { CardActions, CardOverflow } from "@mui/joy";
import { Button } from "primereact/button";
import { ToggleButton } from "primereact/togglebutton";
import useApiService from "../../../../hooks/useApiService";
import { OrganizationChart } from "primereact/organizationchart";
import { OrganogramRecord, TreeNode } from "../../../../models/DSF/OrganogramAndAppointments";
import { useEffect, useRef, useState } from "react";
import "./styles.scss";
import { Toast } from "primereact/toast";
import { TabPanel, TabView } from "primereact/tabview";
import ModernOrganogram from "./ModernOrganogram";
import { transformToReactFlowData } from "./utils/organogramUtils";
import { generateOrganogramHTML } from "../../../../service/organogramService";
import { generatePdf } from "../../../../service/pdfGenerator";

interface ExtendedTreeNode extends TreeNode {
  title: string;
  accessLevel?: any;
  accessLevelName?: any;
  legalReference?: string | null;
  nodeType?: "appointment" | "employee";
  isPromotionNode?: boolean; // For internal restructuring
  employees?: any[]; // Array of employees assigned to this node
}

function Organogram(props: RouteComponentProps) {
  const [treeData, setTreeData] = useState<TreeNode[] | undefined>(undefined);
  const [useModernView, setUseModernView] = useState(true);
  const { setGlobalLoader, session } = props;

  const toast = useRef<any>(null);
  const { data, loading, error } = useApiService<OrganogramRecord>({
    method: "GET",
    url: "site-organogram",
    apiName: "open",
    params: {
      clientId: session.client_id,
      siteId: session.site_id,
    },
  });

  useEffect(() => {
    setGlobalLoader(loading);
  }, [loading, setGlobalLoader]);

  useEffect(() => {
    if (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Could not load site organogram",
        life: 3000,
      });
    }
  }, [error]);
  useEffect(() => {
    if (data) {
      console.log("=== ORGANOGRAM DATA RECEIVED ===");
      console.log("Raw API data:", data);

      // Use the working utility function instead of inline transformation
      const transformedData = transformToReactFlowData(data);

      console.log("=== TRANSFORMED DATA ===");
      console.log("Transformed nodes:", transformedData.nodes);
      console.log("Total nodes:", transformedData.nodes.length);

      // DEBUG: Check transformToReactFlowData output for Assistant to CEO
      const assistantNode = transformedData.nodes.find(
        n => n.id === "dbe3c922-5405-11ef-bb4f-02393f980435"
      );
      console.log("DEBUG: transformToReactFlowData output for Assistant to CEO:", assistantNode);

      // Convert to TreeNode format for OrganizationChart
      const convertToTreeNodes = (nodes: any[]): TreeNode[] => {
        const nodeMap = new Map();

        // First pass: create all nodes
        nodes.forEach(node => {
          nodeMap.set(node.id, {
            key: node.id,
            label: node.data.name,
            data: {
              ...node.data,
              nodeType: "appointment",
              employees: node.data.employees || [],
            },
            children: [],
          });
        });

        // Second pass: build hierarchy
        nodes.forEach(node => {
          if (node.data.parentId && nodeMap.has(node.data.parentId)) {
            const parent = nodeMap.get(node.data.parentId);
            const child = nodeMap.get(node.id);
            parent.children.push(child);
          }
        });

        // Find root nodes (nodes without parents in the data)
        const rootNodes = nodes
          .filter(node => !node.data.parentId || !nodeMap.has(node.data.parentId))
          .map(node => nodeMap.get(node.id));

        return rootNodes;
      };

      const treeNodes = convertToTreeNodes(transformedData.nodes);

      console.log("=== FINAL TREE NODES ===");
      console.log("Tree nodes:", treeNodes);

      // DEBUG: Log employees for Assistant to CEO after treeNodes built
      treeNodes.forEach(node => {
        if (node.label === "Assistant to Chief Executive Officer") {
          console.log(
            "DEBUG [treeNodes]: Final employees for Assistant to CEO:",
            node.data.employees
          );
        }
        console.log(`DEBUG [treeNodes]: Final employees for ${node.label}:`, node.data.employees);
      });

      setTreeData(treeNodes);
    }
  }, [data]);

  const nodeTemplate = (node: ExtendedTreeNode) => {
    console.log("=== NODE TEMPLATE RENDERING ===");
    console.log("Node label:", node.label);
    console.log("Node data:", node.data);
    console.log("Node employees:", node.data?.employees);

    // Get employees from the node data
    const employees = node.data?.employees || [];

    // DEBUG: Log employees in nodeTemplate for every node
    if (node.label === "Assistant to Chief Executive Officer") {
      console.log("DEBUG [nodeTemplate]: Rendering Assistant to CEO, employees:", employees);
    }
    console.log(`DEBUG [nodeTemplate]: Rendering ${node.label}, employees:`, employees);

    return (
      <div
        style={{
          minWidth: "180px",
          minHeight: "120px",
          padding: "10px",
          textAlign: "left",
          border: "1px solid #ddd",
          borderRadius: "8px",
          backgroundColor: "#f8f9fa",
        }}
      >
        {/* Appointment Name */}
        <div style={{ marginBottom: "8px" }}>
          <p style={{ fontSize: "12px", margin: "0", fontWeight: "bold", color: "#333" }}>
            Position:
          </p>
          <p style={{ fontSize: "14px", margin: "2px 0", fontWeight: "bold", color: "#000" }}>
            {node.label}
          </p>
        </div>

        {/* Legal Reference */}
        <div style={{ marginBottom: "8px" }}>
          <p style={{ fontSize: "12px", margin: "0", fontWeight: "bold", color: "#666" }}>
            Legal Reference:
          </p>
          <p style={{ fontSize: "11px", margin: "2px 0", color: "#666" }}>
            {node.data?.legalReference || "—"}
          </p>
        </div>

        {/* Employees */}
        {employees && employees.length > 0 ? (
          <div style={{ marginTop: "8px" }}>
            <p style={{ fontSize: "12px", margin: "0 0 4px 0", fontWeight: "bold", color: "#333" }}>
              Employee{employees.length > 1 ? "s" : ""}:
            </p>
            {employees.map((emp: any, index: number) => (
              <div key={emp.id || index} style={{ marginBottom: "4px" }}>
                <p style={{ fontSize: "11px", margin: "0", fontWeight: "bold", color: "#000" }}>
                  {emp.name} {emp.surname}
                </p>
                {emp.title && (
                  <p style={{ fontSize: "10px", margin: "0", color: "#666", fontStyle: "italic" }}>
                    {emp.title}
                  </p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div style={{ marginTop: "8px" }}>
            <p style={{ fontSize: "11px", margin: "0", fontStyle: "italic", color: "#888" }}>
              No employees assigned
            </p>
          </div>
        )}
      </div>
    );
  };

  // Download functionality for classic organogram
  const handleDownloadPDF = async () => {
    if (!data) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "No organogram data available for PDF generation",
        life: 3000,
      });
      return;
    }

    try {
      setGlobalLoader(true);

      // Get company and site information from session
      const companyName = session.client_name || "Company";
      const siteName = session.site_name || "Site";
      const companyLogo = session.client_logo || "";

      // Transform data for ReactFlow
      const transformedData = transformToReactFlowData(data);

      // Generate HTML content for PDF with ReactFlow layout
      const htmlContent = await generateOrganogramHTML({
        companyName,
        siteName,
        companyLogo,
        nodes: transformedData.nodes,
        edges: transformedData.edges,
        effectiveDate: new Date(),
      });

      // Generate PDF using the existing service
      await generatePdf(htmlContent, `${siteName}_Organogram.pdf`);

      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "PDF generated successfully",
        life: 3000,
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to generate PDF. Please try again.",
        life: 3000,
      });
    } finally {
      setGlobalLoader(false);
    }
  };

  return (
    <>
      {useModernView ? (
        <ModernOrganogram {...props} />
      ) : (
        <TabView>
          <TabPanel header={"Organogram"} key={"organogram"}>
            <CardOverflow style={{ width: "100%" }}>
              <CardActions style={{ marginBottom: 15 }} sx={{ alignSelf: "flex-end", pt: 2 }}>
                <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                  <ToggleButton
                    onLabel="Modern View"
                    offLabel="Classic View"
                    checked={useModernView}
                    onChange={e => setUseModernView(e.value)}
                    className="p-button-sm"
                    style={{ marginRight: 10 }}
                  />

                  <Button
                    name="button"
                    icon="pi pi-save"
                    label="Download PDF"
                    className="p-button-outlined"
                    size="small"
                    style={{
                      fontSize: "small",
                      color: "#2dd36f",
                    }}
                    onClick={handleDownloadPDF}
                  />
                </div>
              </CardActions>
            </CardOverflow>

            {treeData && (
              <div
                className={"card overflow-x-auto"}
                style={{ width: "100%", overflowX: "auto", minHeight: 400 }}
              >
                <OrganizationChart value={treeData} nodeTemplate={nodeTemplate} />
              </div>
            )}
          </TabPanel>
        </TabView>
      )}
      <Toast ref={toast} />
    </>
  );
}

export default Organogram;
